import * as React from 'react';
import { BodyCell } from '@tableau/widgets-datagrid';
import { PlainTextCellWithTransform } from './PlainTextAndNumberCells';
import { FlowOutputStepRunType } from '../../../../ts/models/FlowOutputStepRunInfo';
import { Formatters, Messages } from '../../../../codegen/Localize';
import { SiteUrls } from '@tableau/shared-urls';
import { StyledCellFormatter } from '../StyledCellWrapper';
import {
  FlowOverviewErrorsCellComponent,
  FlowTaskOutputStepsWithIconsCellComponent,
  FlowTaskStatusWithIconsCellComponent
} from '../../../../flow/FlowDataGridDefinitions';
import { RunFlowTaskInfo } from '../../../../ts/models/RunFlowTaskInfo';
import { Duration } from '../../../../common/utilities/Duration';
import { TooltipAnchor } from '../../Tooltips/TooltipAnchor';
import { CommonStyles } from '../../../../common/react/CommonStyles';

export function FlowRunTypeCell(): React.StatelessComponent<BodyCell.Props> {
  return PlainTextCellWithTransform('runType',
      (runType: FlowOutputStepRunType) => {
        return runType === FlowOutputStepRunType.scheduled ? Messages.flowRunHistoryColumn_runTypeScheduled() : Messages.flowRunHistoryColumn_runTypeOndemand();
      }
  );
}

export function FlowRefreshTypeCell(): React.StatelessComponent<BodyCell.Props> {
  return PlainTextCellWithTransform('isIncremental',
      (isIncremental: boolean) => {
        return isIncremental ? Messages.writeOptionsIncremental() : Messages.writeOptionsFull();
      }
  );
}

export function FlowRunDurationCell(
    startTimePropertyName: string,
    endTimePropertyName: string,
    nullDateTimeLabel?: string,
    testId?: string): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
      function DurationCell(props) {
        const startDateTime = props.row.data[startTimePropertyName];
        const endDateTime = props.row.data[endTimePropertyName];
        const duration = new Duration(startDateTime, endDateTime);
        const minimumTwoDigits = Intl.NumberFormat('en-US', {minimumIntegerDigits: 2});
        if (startDateTime == null || endDateTime == null || duration.isNegative) {
          return nullDateTimeLabel == null ? null : <span data-tb-test-id={testId}>{nullDateTimeLabel}</span>;
        }
        return (
            <TooltipAnchor
                width={250}
                delay={0}
                title={Messages.flowRunHistoryColumn_duration()}
                tooltipContent={() => {
                  /* eslint-disable jsx-a11y/anchor-has-content */
                  /* eslint-disable jsx-a11y/anchor-is-valid */
                  return (
                      <div>
                        <a href=""/>
                        <div className={'tb-content-item-popover-title'}>
                          {Messages.flowRunHistoryColumn_duration_title()}
                        </div>
                        <div className={'tb-content-item-popover-description'}>
                          {duration.hours > 0 ?
                              Messages.flowRunHistoryColumn_duration_message_hoursMinutesSeconds({
                                HOURS_COUNT: duration.hours,
                                HOURS_LOC: Formatters.formatCount(duration.hours),
                                MINUTES_COUNT: duration.minutes,
                                MINUTES_LOC: Formatters.formatCount(duration.minutes),
                                SECONDS_COUNT: duration.seconds,
                                SECONDS_LOC: Formatters.formatCount(duration.seconds),
                              }) : duration.minutes > 0 ?
                                  Messages.flowRunHistoryColumn_duration_message_minutesSeconds({
                                    MINUTES_COUNT: duration.minutes,
                                    MINUTES_LOC: Formatters.formatCount(duration.minutes),
                                    SECONDS_COUNT: duration.seconds,
                                    SECONDS_LOC: Formatters.formatCount(duration.seconds),
                                  }) :
                                  Messages.flowRunHistoryColumn_duration_message_seconds({
                                    SECONDS_COUNT: duration.seconds,
                                    SECONDS_LOC: Formatters.formatCount(duration.seconds),
                                  })
                          }
                        </div>
                      </div>
                  );
                }
                }
            >
              <div data-tb-test-id={testId} style={CommonStyles.Layout.TextEllipsis}>
                {
                  minimumTwoDigits.format(duration.hours) + ':' +
                  minimumTwoDigits.format(duration.minutes) + ':' +
                  minimumTwoDigits.format(duration.seconds)
                }
              </div>
            </TooltipAnchor>
        );
      }
  );
}

export function FlowTaskOutputStepsWithIconsCell(): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
      function FlowTaskOutputStepsWithIconsCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
            <FlowTaskOutputStepsWithIconsCellComponent flowId={data.flowId} outputSteps={data.flowOutputSteps}/>
        );
      }
  );
}

export function FlowTaskStatusWithIconsCell(siteUrls: SiteUrls, flowFailureThresholdForRunPrevention: number): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
      function FlowTaskStatusWithIconsCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
            <FlowTaskStatusWithIconsCellComponent
                flowId={data.flowId}
                siteUrls={siteUrls}
                runFlowTaskInfo={data as RunFlowTaskInfo}
                flowFailureThresholdForRunPrevention={flowFailureThresholdForRunPrevention}
            />
        );
      }
  );
}

export function FlowRunHistoryErrorsCell(siteUrls: SiteUrls, isDownloadAllowed: boolean): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
      function FlowRunHistoryErrorsCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
            <FlowOverviewErrorsCellComponent
                errors={data.flowRunErrors}
                flowId={data.flowId}
                onConnectionsPage={false}
                siteUrls={siteUrls}
                isDownloadAllowed={isDownloadAllowed}
            />
        );
      }
  );
}
